// Small voxel models built from code: swords, accessories, pickups, mobs and pets.
import * as THREE from 'three';
import { hexToRgb } from './util.js';
import { SWORD_SHAPE, makeCoinTexture, tileUV } from './textures.js';

const FACES = [
  { n: [1, 0, 0], v: [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]], s: 0.88 },
  { n: [-1, 0, 0], v: [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]], s: 0.88 },
  { n: [0, 1, 0], v: [[0, 1, 1], [1, 1, 1], [1, 1, 0], [0, 1, 0]], s: 1.0 },
  { n: [0, -1, 0], v: [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]], s: 0.72 },
  { n: [0, 0, 1], v: [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]], s: 0.94 },
  { n: [0, 0, -1], v: [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]], s: 0.94 },
];

const colorCache = new Map();
function rgb01(hex) {
  if (!colorCache.has(hex)) {
    const c = new THREE.Color(hex);
    colorCache.set(hex, [c.r, c.g, c.b]);
  }
  return colorCache.get(hex);
}

// voxels: [[x,y,z,'#hex' | '!#hex'(glow)], ...]. Returns a Group with lit + glow meshes.
export function voxelModel(voxels, unit = 1 / 16, { center = [0, 0, 0], castShadow = true, glowBoost = 1.6 } = {}) {
  const map = new Map();
  for (const v of voxels) map.set(`${v[0]},${v[1]},${v[2]}`, v[3]);
  const bufs = { lit: { p: [], n: [], c: [], i: [] }, glow: { p: [], n: [], c: [], i: [] } };
  for (const [x, y, z, col] of voxels) {
    const glow = col.startsWith('!');
    const hex = glow ? col.slice(1) : col;
    const base = rgb01(hex);
    const B = glow ? bufs.glow : bufs.lit;
    for (const f of FACES) {
      if (map.has(`${x + f.n[0]},${y + f.n[1]},${z + f.n[2]}`)) continue;
      const vi = B.p.length / 3;
      const k = glow ? glowBoost : f.s;
      for (const c of f.v) {
        B.p.push((x + c[0] - center[0]) * unit, (y + c[1] - center[1]) * unit, (z + c[2] - center[2]) * unit);
        B.n.push(f.n[0], f.n[1], f.n[2]);
        B.c.push(base[0] * k, base[1] * k, base[2] * k);
      }
      B.i.push(vi, vi + 1, vi + 2, vi, vi + 2, vi + 3);
    }
  }
  const group = new THREE.Group();
  for (const key of ['lit', 'glow']) {
    const B = bufs[key];
    if (!B.p.length) continue;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(B.p, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(B.n, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(B.c, 3));
    g.setIndex(B.i);
    g.computeBoundingSphere();
    const mat = key === 'lit' ? new THREE.MeshLambertMaterial({ vertexColors: true }) : new THREE.MeshBasicMaterial({ vertexColors: true });
    const m = new THREE.Mesh(g, mat);
    m.castShadow = castShadow && key === 'lit';
    group.add(m);
  }
  return group;
}

// Parse layered pixel art: layers = [['row', ...] per y (top row first)], palette letter -> color
export function artToVoxels(rows, palette, z0 = 0, depth = 1) {
  const out = [];
  const h = rows.length;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < rows[y].length; x++) {
      const ch = rows[y][x];
      if (ch === '.' || ch === ' ') continue;
      const col = palette[ch];
      if (!col) continue;
      for (let z = 0; z < depth; z++) out.push([x, h - 1 - y, z0 + z, col]);
    }
  return out;
}

// ---------- swords ----------
export const SWORD_PALETTES = {
  wood: { '#': '#3a2810', w: '#c49a60', l: '#8f6a3a', g: '#6b4a2a', h: '#5a3c20', p: '#6b4a2a' },
  stone: { '#': '#2a2a2e', w: '#a8a8ac', l: '#76767a', g: '#6b4a2a', h: '#5a3c20', p: '#6b4a2a' },
  iron: { '#': '#2a2a2e', w: '#f0f0f0', l: '#bdbdc2', g: '#6b4a2a', h: '#5a3c20', p: '#8a8a90' },
  gold: { '#': '#5a3a08', w: '#fff07a', l: '#f0b828', g: '#c98a10', h: '#5a3c20', p: '#f0b828' },
  diamond: { '#': '#0e3a3a', w: '#a6fff4', l: '#38d4c8', g: '#1f8a90', h: '#5a3c20', p: '#38d4c8' },
  ruby: { '#': '#3a0a10', w: '#ff9aa6', l: '#e02a46', g: '#8a1a2a', h: '#3a2a2a', p: '#e02a46' },
  ice: { '#': '#1a3a5a', w: '!#e8f8ff', l: '#8fd0ff', g: '#5a9ad0', h: '#3a5a7a', p: '#8fd0ff' },
  lava: { '#': '#3a0a00', w: '!#ffd060', l: '!#ff6a10', g: '#3a2a2a', h: '#2a1a1a', p: '!#ff6a10' },
  lightning: { '#': '#5a4a00', w: '!#fffbb0', l: '!#ffd020', g: '#3a6aff', h: '#2a2a4a', p: '!#7aa0ff' },
  glitch: { '#': '#1a0a2a', w: '!#ff6ae8', l: '!#30e8ff', g: '#5b1a8f', h: '#14101c', p: '!#ff2bd6' },
  rainbow: { '#': '#2a2a2a', w: '!#ffffff', l: '!#ff4d6d', g: '#ffd23f', h: '#5a3c20', p: '!#4d9de0' },
  crystal: { '#': '#1c1446', w: '!#e6e0ff', l: '!#9a8cff', g: '#5b46c8', h: '#2a2252', p: '!#7fdcff' },
};

export function swordModel(kind = 'wood') {
  const pal = SWORD_PALETTES[kind] || SWORD_PALETTES.wood;
  let vox = artToVoxels(SWORD_SHAPE, pal, 0, 1);
  if (kind === 'rainbow') {
    const cols = ['!#ff4d6d', '!#ffb020', '!#ffe03b', '!#4bd34b', '!#3bb0ff', '!#a05cff'];
    vox = vox.map((v) => (SWORD_SHAPE[15 - v[1]][v[0]] === 'w' || SWORD_SHAPE[15 - v[1]][v[0]] === 'l' ? [v[0], v[1], v[2], cols[(v[0] + v[1]) % 6]] : v));
  }
  // grip pixel is at x=2, y=2 (from bottom)
  const g = voxelModel(vox, 0.046, { center: [2.5, 2.5, 0.5] });
  return g;
}

// ---------- accessories (head-pixel space: head spans x -4..4, y 0..8, z -4..4) ----------
export function crownModel() {
  const v = [];
  const gold = '#f5c542', dark = '#c9951a';
  for (let x = -5; x < 5; x++)
    for (let z = -5; z < 5; z++) {
      const edge = x === -5 || x === 4 || z === -5 || z === 4;
      if (!edge) continue;
      v.push([x, 8, z, dark], [x, 9, z, gold]);
      const spike = (x === -5 || x === 4) && (z === -5 || z === 4 || z === -1 || z === 0) || (z === -5 || z === 4) && (x === -1 || x === 0);
      if (spike) v.push([x, 10, z, gold]);
    }
  v.push([-1, 9, 4, '!#ff3355'], [0, 9, 4, '!#ff3355'], [-5, 9, -1, '!#33ccff'], [4, 9, 0, '!#33ccff']);
  return voxelModel(v, 1.8 / 32);
}
export function headphonesModel() {
  const v = [];
  for (let x = -5; x <= 4; x++) v.push([x, 8, 0, '#222228']);
  v.push([-5, 7, 0, '#222228'], [4, 7, 0, '#222228']);
  for (let y = 2; y <= 6; y++)
    for (let z = -1; z <= 1; z++) {
      v.push([-6, y, z, y === 4 && z === 0 ? '!#33ff88' : '#e03030'], [5, y, z, y === 4 && z === 0 ? '!#33ff88' : '#e03030']);
    }
  return voxelModel(v, 1.8 / 32);
}
export function antennaModel() {
  const v = [];
  for (let y = 8; y < 11; y++) v.push([0, y, 0, '#5a6270']);
  v.push([0, 11, 0, '!#40e8ff'], [-1, 11, 0, '!#40e8ff'], [0, 12, 0, '!#40e8ff']);
  return voxelModel(v, 1.8 / 32);
}
export function haloModel() {
  const v = [];
  for (let x = -4; x < 4; x++)
    for (let z = -4; z < 4; z++) if (x === -4 || x === 3 || z === -4 || z === 3) v.push([x, 11, z, '!#ffe680']);
  return voxelModel(v, 1.8 / 32);
}

// ---------- pickups ----------
let coinAssets = null;
export function getCoinAssets() {
  if (coinAssets) return coinAssets;
  const tex = makeCoinTexture();
  const geo = new THREE.CylinderGeometry(0.34, 0.34, 0.09, 20);
  geo.rotateX(Math.PI / 2);
  const side = new THREE.MeshLambertMaterial({ color: '#d99a18', emissive: '#7a4a00', emissiveIntensity: 0.6 });
  const face = new THREE.MeshLambertMaterial({ map: tex, emissive: '#ffcc33', emissiveMap: tex, emissiveIntensity: 0.55 });
  coinAssets = { geo, mats: [side, face, face], tex };
  return coinAssets;
}

export function gemModel(color = '#4ff0ff') {
  const geo = new THREE.OctahedronGeometry(0.3, 0);
  geo.scale(1, 1.35, 1);
  const mat = new THREE.MeshPhongMaterial({ color, emissive: color, emissiveIntensity: 0.55, shininess: 120, flatShading: true, specular: '#ffffff' });
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

export function starModel(color = '#ffd23f', size = 0.5) {
  const shape = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? size : size * 0.45;
    const a = (i / 10) * Math.PI * 2 + Math.PI / 2;
    const x = Math.cos(a) * r, y = Math.sin(a) * r;
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: size * 0.3, bevelEnabled: true, bevelThickness: size * 0.12, bevelSize: size * 0.1, bevelSegments: 2 });
  geo.center();
  const mat = new THREE.MeshPhongMaterial({ color, emissive: color, emissiveIntensity: 0.7, shininess: 90, specular: '#ffffff' });
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

const HEART_ART = ['.##.##.', '#######', '#######', '.#####.', '..###..', '...#...'];
export function heartModel() {
  const vox = artToVoxels(HEART_ART, { '#': '!#ff3a5a' }, 0, 2);
  vox.push([1, 4, 2, '!#ffffff'], [2, 5, 2, '!#ffb0c0']);
  return voxelModel(vox, 0.085, { center: [3.5, 3, 1] });
}

const BOLT_ART = ['..###', '.###.', '###..', '.###.', '..##.', '.##..', '##...'];
export function powerupModel(kind) {
  const colors = { magnet: '!#ff4d4d', speed: '!#ffd23f', jump: '!#7be35a', shield: '!#6fd3ff' };
  if (kind === 'magnet') {
    const art = ['##..##', '##..##', '##..##', '##..##', '######', '.####.'];
    const vox = artToVoxels(art, { '#': '!#ff4d4d' }, 0, 2);
    for (const v of vox) if (v[1] === 5) v[3] = '!#e8e8e8';
    return voxelModel(vox, 0.08, { center: [3, 3, 1] });
  }
  const vox = artToVoxels(BOLT_ART, { '#': colors[kind] || colors.speed }, 0, 2);
  return voxelModel(vox, 0.08, { center: [2.5, 3.5, 1] });
}

// ---------- block-textured boxes ----------
// Box with atlas tiles per face: {top, bottom, side, front, back}
export function atlasBox(w, h, d, tiles, uvScale = null) {
  const g = new THREE.BoxGeometry(w, h, d);
  const order = [tiles.side, tiles.side, tiles.top, tiles.bottom ?? tiles.top, tiles.front ?? tiles.side, tiles.back ?? tiles.side];
  const uv = g.attributes.uv;
  for (let f = 0; f < 6; f++) {
    const r = tileUV(order[f]);
    for (let k = 0; k < 4; k++) {
      const i = f * 4 + k;
      let s = uv.getX(i), t = uv.getY(i);
      if (uvScale) {
        s = 0.5 + (s - 0.5) * uvScale[f][0];
        t = 0.5 + (t - 0.5) * uvScale[f][1];
      }
      uv.setXY(i, r.u0 + s * (r.u1 - r.u0), r.v0 + t * (r.v1 - r.v0));
    }
  }
  // the shared block material uses vertex colours (for ambient occlusion) — give these boxes plain white
  g.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(g.attributes.position.count * 3).fill(1), 3));
  return g;
}

// ---------- mobs ----------
export function slimeModel(color = '#6fd65a', core = '#3f9a32', size = 0.8) {
  const g = new THREE.Group();
  const outer = new THREE.Mesh(
    new THREE.BoxGeometry(size, size, size),
    new THREE.MeshLambertMaterial({ color, transparent: true, opacity: 0.72, emissive: color, emissiveIntensity: 0.15, depthWrite: false })
  );
  outer.position.y = size / 2;
  outer.renderOrder = 2;
  const inner = new THREE.Mesh(new THREE.BoxGeometry(size * 0.5, size * 0.5, size * 0.5), new THREE.MeshLambertMaterial({ color: core }));
  inner.position.y = size * 0.42;
  inner.castShadow = true;
  const eyeMat = new THREE.MeshBasicMaterial({ color: '#10200c' });
  const eyeG = new THREE.BoxGeometry(size * 0.15, size * 0.2, 0.02);
  const e1 = new THREE.Mesh(eyeG, eyeMat);
  const e2 = new THREE.Mesh(eyeG, eyeMat);
  e1.position.set(-size * 0.2, size * 0.6, size / 2 + 0.011);
  e2.position.set(size * 0.2, size * 0.6, size / 2 + 0.011);
  const shine = new THREE.Mesh(new THREE.BoxGeometry(size * 0.06, size * 0.07, 0.02), new THREE.MeshBasicMaterial({ color: '#ffffff' }));
  shine.position.set(-size * 0.17, size * 0.64, size / 2 + 0.022);
  const mouth = new THREE.Mesh(new THREE.BoxGeometry(size * 0.12, size * 0.08, 0.02), eyeMat);
  mouth.position.set(size * 0.05, size * 0.33, size / 2 + 0.011);
  g.add(outer, inner, e1, e2, shine, mouth);
  g.userData.outer = outer;
  return g;
}

export function boomBotModel() {
  const v = [];
  const body = '#b8402e', dark = '#6a2418', stripe = '#ffd23f', black = '#1a1a1e';
  for (let x = 0; x < 8; x++)
    for (let y = 3; y < 11; y++)
      for (let z = 0; z < 8; z++) {
        let c = body;
        if (y === 6 || y === 7) c = (x + z + y) % 2 === 0 ? stripe : black;
        if (y === 10) c = dark;
        v.push([x, y, z, c]);
      }
  // face: angry glowing eyes on front (z=7)
  for (const [x, y] of [[1, 8], [2, 8], [2, 9], [5, 8], [5, 9], [6, 8]]) v.push([x, y, 8, '!#ffe24a']);
  for (let x = 2; x < 6; x++) v.push([x, 4, 8, black]);
  // fuse
  v.push([4, 11, 4, '#3a3a40'], [4, 12, 4, '#3a3a40'], [4, 13, 4, '!#ffb020']);
  const g = voxelModel(v, 1 / 13, { center: [4, 0, 4] });
  const legs = [];
  const legGeo = new THREE.BoxGeometry(2 / 13, 3 / 13, 2 / 13);
  const legMat = new THREE.MeshLambertMaterial({ color: '#2a2a30' });
  for (const [x, z] of [[-2.5, -2.5], [2.5, -2.5], [-2.5, 2.5], [2.5, 2.5]]) {
    const l = new THREE.Mesh(legGeo, legMat);
    l.position.set(x / 13, 1.5 / 13, z / 13);
    l.castShadow = true;
    g.add(l);
    legs.push(l);
  }
  g.userData.legs = legs;
  return g;
}

export function flyerModel(kind = 'fire') {
  const pal = kind === 'glitch' ? { b: '#1a1424', e: '!#ff2bd6', w: '#5b1a8f', a: '!#22e8ff' } : kind === 'ice' ? { b: '#9ad8ff', e: '!#1a4a9a', w: '#e8f8ff', a: '!#ffffff' } : { b: '#5a1a10', e: '!#ffd040', w: '#c8401a', a: '!#ff8a20' };
  const v = [];
  for (let x = 0; x < 6; x++) for (let y = 0; y < 5; y++) for (let z = 0; z < 6; z++) v.push([x, y, z, y === 4 && (x + z) % 3 === 0 ? pal.a : pal.b]);
  v.push([1, 3, 6, pal.e], [4, 3, 6, pal.e], [2, 1, 6, pal.w], [3, 1, 6, pal.w]);
  const body = voxelModel(v, 1 / 14, { center: [3, 2.5, 3] });
  const wingArt = ['####', '###.', '##..'];
  const wl = voxelModel(artToVoxels(wingArt, { '#': pal.w }, 0, 1), 1 / 14, { center: [4, 1.5, 0.5] });
  const wr = voxelModel(artToVoxels(wingArt.map((r) => r.split('').reverse().join('')), { '#': pal.w }, 0, 1), 1 / 14, { center: [0, 1.5, 0.5] });
  const wingL = new THREE.Group();
  wingL.add(wl);
  wl.rotation.x = -Math.PI / 2;
  wingL.position.set(-3 / 14, 0.05, 0);
  const wingR = new THREE.Group();
  wingR.add(wr);
  wr.rotation.x = -Math.PI / 2;
  wingR.position.set(3 / 14, 0.05, 0);
  const g = new THREE.Group();
  g.add(body, wingL, wingR);
  g.userData.wings = [wingL, wingR];
  return g;
}

// ---------- pets ----------
export function petModel(kind) {
  const g = new THREE.Group();
  if (kind === 'dog') {
    const box = (w, h, d, colour, x, y, z, parent = g) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshLambertMaterial({ color: colour }));
      m.position.set(x, y, z); m.castShadow = true; parent.add(m); return m;
    };
    box(0.44, 0.34, 0.7, '#c48d52', 0, 0.43, 0);
    box(0.4, 0.4, 0.38, '#dca86c', 0, 0.7, 0.4);
    box(0.27, 0.17, 0.21, '#f5dfbb', 0, 0.59, 0.65);
    box(0.12, 0.1, 0.03, '#2a242c', 0, 0.64, 0.77);
    for (const x of [-0.14, 0.14]) {
      box(0.13, 0.33, 0.18, '#7c5033', x * 1.55, 0.68, 0.35);
      box(0.055, 0.07, 0.03, '#242638', x, 0.76, 0.601);
    }
    box(0.45, 0.08, 0.14, '#4fbddd', 0, 0.58, 0.23);
    const legs = [];
    for (const x of [-0.16, 0.16]) for (const z of [-0.23, 0.24]) legs.push(box(0.12, 0.28, 0.12, '#f5dfbb', x, 0.17, z));
    const tail = new THREE.Group(); tail.position.set(0, 0.52, -0.3); g.add(tail);
    box(0.09, 0.12, 0.36, '#7c5033', 0, 0.09, -0.16, tail);
    g.userData.legs = legs; g.userData.tail = tail;
  } else if (kind === 'drone') {
    const v = [];
    for (let x = 0; x < 6; x++) for (let y = 0; y < 3; y++) for (let z = 0; z < 6; z++) v.push([x, y, z, y === 2 ? '#c8d0dc' : '#8a94a4']);
    v.push([2, 1, 6, '!#40e8ff'], [3, 1, 6, '!#40e8ff']);
    for (const [x, z] of [[-1, -1], [6, -1], [-1, 6], [6, 6]]) v.push([x, 2, z, '#5a6270']);
    g.add(voxelModel(v, 1 / 16, { center: [3, 1.5, 3] }));
    const rotors = [];
    const rg = new THREE.BoxGeometry(0.28, 0.015, 0.04);
    const rm = new THREE.MeshBasicMaterial({ color: '#dfe6f0' });
    for (const [x, z] of [[-3.5, -3.5], [3.5, -3.5], [-3.5, 3.5], [3.5, 3.5]]) {
      const r = new THREE.Mesh(rg, rm);
      r.position.set(x / 16, 2.2 / 16, z / 16);
      g.add(r);
      rotors.push(r);
    }
    g.userData.rotors = rotors;
  } else if (kind === 'slime') {
    g.add(slimeModel('#5ab4ff', '#2a6ab0', 0.42));
    g.children[0].position.y = -0.2;
  } else if (kind === 'ghost') {
    const m = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.42, 0.42), new THREE.MeshLambertMaterial({ color: '#ffffff', transparent: true, opacity: 0.75, emissive: '#bfe8ff', emissiveIntensity: 0.35 }));
    g.add(m);
    const em = new THREE.MeshBasicMaterial({ color: '#1a1a2a' });
    for (const x of [-0.1, 0.1]) {
      const e = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.11, 0.02), em);
      e.position.set(x, 0.05, 0.215);
      g.add(e);
    }
    const mo = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 0.02), em);
    mo.position.set(0, -0.08, 0.215);
    g.add(mo);
  } else if (kind === 'golem') {
    const v = [];
    for (let x = 0; x < 6; x++) for (let y = 0; y < 7; y++) for (let z = 0; z < 5; z++) v.push([x, y, z, (x * 3 + y * 5 + z) % 7 === 0 ? '#5a9a3a' : y > 3 ? '#9a9a9e' : '#86868a']);
    v.push([1, 5, 5, '!#7cff6a'], [4, 5, 5, '!#7cff6a']);
    for (let y = 1; y < 5; y++) v.push([-1, y, 2, '#86868a'], [6, y, 2, '#86868a']);
    g.add(voxelModel(v, 1 / 14, { center: [3, 3.5, 2.5] }));
  } else if (kind === 'dragon') {
    const v = [];
    const R = '#d8342a', O = '#ffb040', D = '#8a1a14';
    for (let x = 0; x < 4; x++) for (let y = 0; y < 4; y++) for (let z = 0; z < 6; z++) v.push([x, y, z, y === 0 && z > 0 && z < 5 ? O : R]);
    for (let x = 0; x < 4; x++) for (let y = 2; y < 5; y++) for (let z = 6; z < 9; z++) v.push([x, y, z, R]);
    v.push([0, 4, 8, '!#ffe24a'], [3, 4, 8, '!#ffe24a'], [0, 5, 6, '#f0e0c0'], [3, 5, 6, '#f0e0c0'], [1, 2, 9, D], [2, 2, 9, D]);
    for (let z = -3; z < 0; z++) v.push([1, 1, z, R], [2, 1, z, R]);

    g.add(voxelModel(v, 1 / 16, { center: [2, 2, 3] }));
    const wings = [];
    for (const side of [-1, 1]) {
      const wing = new THREE.Group(); wing.position.set(side * 0.13, 0.1, 0);
      const membrane = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.025, 0.3), new THREE.MeshLambertMaterial({ color: '#f89144' }));
      membrane.position.x = side * 0.2; membrane.castShadow = true; wing.add(membrane); g.add(wing); wings.push(wing);
    }
    g.userData.wings = wings; g.scale.setScalar(1.65);
  } else if (kind === 'lucky') {
    const geo = atlasBox(0.42, 0.42, 0.42, { top: 'lucky', side: 'lucky' });
    const m = new THREE.Mesh(geo, getAtlasMaterial());
    m.castShadow = true;
    g.add(m);
    const em = new THREE.MeshBasicMaterial({ color: '#2a1a00' });
    for (const x of [-0.1, 0.1]) {
      const e = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.09, 0.02), em);
      e.position.set(x, 0.07, 0.215);
      g.add(e);
    }
  } else if (kind === 'tnt') {
    const v = [];
    for (let x = 0; x < 6; x++) for (let y = 0; y < 6; y++) for (let z = 0; z < 6; z++) v.push([x, y, z, y === 2 || y === 3 ? '#f2f2f2' : '#d83a2a']);
    v.push([1, 4, 6, '#1a1a1a'], [4, 4, 6, '#1a1a1a'], [3, 6, 3, '!#ffb020']);
    g.add(voxelModel(v, 1 / 14, { center: [3, 3, 3] }));
  }
  return g;
}

let atlasMat = null;
export function setAtlasMaterial(m) {
  atlasMat = m;
}
export function getAtlasMaterial() {
  return atlasMat;
}

// Simple canvas sprite for name tags
export function nameTag(text, { color = '#ececec', crown = true, scale = 1 } = {}) {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 128;
  const ctx = c.getContext('2d');
  ctx.font = '72px "Lilita One", "Arial Black", sans-serif';
  const tw = ctx.measureText(text).width;
  const w = Math.min(500, tw + (crown ? 130 : 70));
  const x0 = (512 - w) / 2;
  ctx.fillStyle = 'rgba(10,14,24,0.62)';
  roundRect(ctx, x0, 14, w, 100, 30);
  ctx.fill();
  let tx = 256;
  if (crown) {
    tx += 30;
    const cx = x0 + 34, cy = 40;
    ctx.fillStyle = '#ffd23f';
    ctx.beginPath();
    ctx.moveTo(cx, cy + 44);
    ctx.lineTo(cx, cy + 8);
    ctx.lineTo(cx + 13, cy + 26);
    ctx.lineTo(cx + 25, cy);
    ctx.lineTo(cx + 37, cy + 26);
    ctx.lineTo(cx + 50, cy + 8);
    ctx.lineTo(cx + 50, cy + 44);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#ff3355';
    ctx.fillRect(cx + 21, cy + 30, 8, 8);
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 10;
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.strokeText(text, tx, 68, crown ? 360 : 450);
  ctx.fillStyle = color;
  ctx.fillText(text, tx, 68, crown ? 360 : 450);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, transparent: true });
  const s = new THREE.Sprite(mat);
  s.scale.set(1.6 * scale, 0.4 * scale, 1);
  s.renderOrder = 50;
  return s;
}

export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
